/**
 * El idioma de quien está usando el editor.
 *
 * ── Cuál de los dos idiomas ─────────────────────────────────────────────────
 * En el editor conviven dos, y confundirlos es el fallo caro:
 *
 *   · El de la PERSONA — este archivo. Los rótulos del editor, la barra, el
 *     inspector, los avisos. Viaja con quien inició sesión y vale igual en
 *     todos sus proyectos. Sale de `CATALOGS`, cargado por idioma (ver `loadEditorCatalog`).
 *
 *   · El del PROYECTO — `SITE_CATALOGS`, y no se toca desde aquí. Es lo que
 *     acaba DENTRO de la página publicada: los rótulos de un formulario, los
 *     mensajes de error de una reserva, el aviso de cookies. Ese lo lee el
 *     visitante del sitio, no quien lo construye.
 *
 * Una agencia de Miami monta en inglés un sitio que atiende en español. Meter
 * una frase en el catálogo equivocado no rompe nada aquí y aparece semanas
 * después en el sitio de un cliente, en el idioma de otra persona. Ver
 * `catalogBoundary.test.ts`, que fija la frontera.
 *
 * ── Dónde se guarda ─────────────────────────────────────────────────────────
 * En `@webbuilder/i18n`: cookie compartida de `.veltica.com` más copia en
 * `localStorage`. Por eso cruzar del panel al editor no cambia de idioma, igual
 * que no cambia de color. Aquí no hay selector — se elige en un sitio, que es el
 * panel, y el editor obedece.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_LANGUAGE,
  createTranslator,
  formatters,
  onLanguageChange,
  persistLanguage,
  readLanguage,
  type Catalog,
  type Catalogs,
  type Formatters,
  type Language,
  type Translate,
} from '@webbuilder/i18n'

/**
 * Los textos del editor, sólo del idioma que se usa.
 *
 * Eran los dos catálogos enteros —unos 980 KB de texto, español e inglés— en
 * el paquete que descarga el editor al abrir, cuando cada persona usa uno. Se
 * pide el suyo antes de pintar (`main.tsx`) y el otro sólo si cambia de idioma.
 * `createTranslator` tolera un idioma ausente (usa `{}`), así que un catálogo
 * que todavía no llegó sólo dejaría ver la clave, nunca un fallo.
 */
const loaded: Partial<Record<Language, Catalog>> = {}
const catalogs = (): Catalogs => loaded as Catalogs

/**
 * Hace `apply` en cuanto el catálogo está: en el acto si ya se cargó —cambiar a
 * un idioma ya visto no espera ni un ciclo—, o al llegar si hay que pedirlo.
 */
function whenCatalog(language: Language, apply: () => void): void {
  if (loaded[language]) apply()
  else void loadEditorCatalog(language).then(apply, apply)
}

export async function loadEditorCatalog(language: Language): Promise<void> {
  if (loaded[language]) return
  if (language === 'en') loaded.en = (await import('@webbuilder/i18n/catalog/en')).en
  else loaded.es = (await import('@webbuilder/i18n/catalog/es')).es
}

/**
 * El idioma vigente fuera de React.
 *
 * Hay mucho código del editor que no es un componente y aun así escribe texto
 * que se lee: el dibujado del lienzo, el almacén, los ayudantes que arman
 * marcado. Ninguno puede usar un hook. Vale porque sólo hay una persona editando
 * a la vez.
 */
let currentLanguage: Language | null = null

/**
 * El idioma vigente, sepa o no React que existe.
 *
 * Se resuelve la primera vez que se pregunta y se refresca cuando cambia. Antes
 * arrancaba en el idioma por defecto y sólo se corregía al montar el proveedor:
 * cualquier `translate` que ocurriera antes —o fuera del árbol de React, como al
 * instanciar un bloque— hablaba en español aunque la persona tuviera el editor
 * en inglés. No era teórico: lo encontró la prueba de los planos.
 */
export function activeLanguage(): Language {
  if (currentLanguage === null) currentLanguage = readLanguage()
  return currentLanguage
}

/* Y se entera de los cambios por su cuenta, venga de esta pestaña o de otra:
   primero llega el catálogo nuevo, después cambia el idioma. */
if (typeof window !== 'undefined') onLanguageChange(() => {
  const next = readLanguage()
  whenCatalog(next, () => { currentLanguage = next })
})

/**
 * El traductor FUERA de un componente.
 *
 * Se construye en cada llamada a propósito: el idioma puede haber cambiado desde
 * la anterior, y un traductor guardado se quedaría en el de antes.
 */
export function translate(key: string, vars?: Record<string, string | number>): string {
  return createTranslator(activeLanguage(), catalogs())(key, vars)
}

/**
 * El idioma del SITIO, que no es el del editor.
 *
 * Lo que se queda escrito en la página —los textos de muestra de un bloque
 * recién insertado, la barra y el pie automáticos, las etiquetas de un
 * formulario— es contenido del sitio, y va en su idioma. Antes salía en el de
 * quien edita: con el editor en inglés, un sitio en español nacía con «Get
 * started» en la barra y «All rights reserved» en el pie.
 *
 * Lo fija el editor al saber el idioma del proyecto (`setSiteLanguage`), y una
 * operación puntual puede fijarlo para sí (`withSiteLanguage`): la barra de un
 * sitio recién hecho se arma antes de que llegue el del proyecto.
 */
let siteLanguage: Language | null = null

export function setSiteLanguage(language: Language): void {
  siteLanguage = language
  void loadEditorCatalog(language)
}

export function withSiteLanguage<T>(language: Language, run: () => T): T {
  const previous = siteLanguage
  siteLanguage = language
  try {
    return run()
  } finally {
    siteLanguage = previous
  }
}

/**
 * Traduce un texto que se queda en el sitio. Si el catálogo de su idioma aún no
 * ha llegado, el del editor: nunca la clave cruda.
 */
export function translateForSite(key: string, vars?: Record<string, string | number>): string {
  const language = siteLanguage && loaded[siteLanguage] ? siteLanguage : activeLanguage()
  return createTranslator(language, catalogs())(key, vars)
}

/**
 * Un error del servidor, dicho en el idioma de quien edita.
 *
 * ── Por qué el editor lo necesita aparte ────────────────────────────────────
 * El panel ya sabía hacer esto; el editor no tenía nada. Así que un fallo del
 * asistente AI —el sitio donde más errores se ven, porque se pide algo y a veces
 * no sale— llegaba siempre en español y se enseñaba tal cual. Con el editor en
 * inglés eso deja una frase española en mitad de la conversación.
 *
 * Es una copia deliberada de `translateServerError` del panel y no un módulo
 * compartido: las dos aplicaciones construyen su traductor con catálogos
 * distintos y con su propia idea de cuál es el idioma vigente. Compartir la
 * función obligaría a pasarle las dos cosas, que es más enredo que las diez
 * líneas que ahorra.
 *
 * Prefiere el CÓDIGO al texto: el worker manda los dos y el texto viene siempre
 * en español. Sin código traducible cae al texto del servidor, que se entiende
 * aunque esté en el otro idioma, y si tampoco hay, a lo que pase quien llama.
 */
export function serverErrorCode(error: unknown): string | null {
  const raw = error instanceof Error ? error.message : ''
  return /"code":"([a-zA-Z.]+)"/.exec(raw)?.[1] ?? null
}

/**
 * Con qué rellenar los huecos de ese texto.
 *
 * El worker manda los valores aparte porque su frase y la traducida no son la
 * misma: «Tu plan permite 3» allí, «Your plan allows {limit}» aquí. Se descarta
 * entero lo que no encaje —un `JSON.parse` suelto dentro de un `catch` convierte
 * un mensaje feo en una pantalla en blanco.
 */
export function serverErrorVars(error: unknown): Record<string, string | number> | undefined {
  const raw = error instanceof Error ? error.message : ''
  const found = /"vars":(\{[^{}]*\})/.exec(raw)?.[1]
  if (!found) return undefined
  try {
    const parsed: unknown = JSON.parse(found)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined
    const vars: Record<string, string | number> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string' || typeof value === 'number') vars[key] = value
    }
    return Object.keys(vars).length > 0 ? vars : undefined
  } catch {
    return undefined
  }
}

export function translateServerError(error: unknown, fallback: string): string {
  const code = serverErrorCode(error)
  if (code) {
    const key = `error.${code}`
    const translated = translate(key, serverErrorVars(error))
    /* `translate` devuelve la clave cuando no la conoce: el servidor mandó un
       código que este editor todavía no sabe decir, y entonces vale más su
       texto que un identificador en pantalla. */
    if (translated !== key) return translated
  }
  const raw = error instanceof Error ? error.message : ''
  return /\{"message":"([^"]+)"/.exec(raw)?.[1]
    ?? /\{"error":"([^"]+)"/.exec(raw)?.[1]
    ?? (raw || fallback)
}

type LanguageValue = {
  language: Language
  t: Translate
  fmt: Formatters
  /**
   * Cambiar el idioma del panel DESDE el editor.
   *
   * Antes sólo se podía en el panel y aquí se escuchaba el cambio; desde que el
   * menú de la cuenta es el mismo en las dos aplicaciones, la fila del idioma
   * también está aquí y tiene que poder escribir. Persiste en el mismo sitio
   * —`persistLanguage`— así que la otra pestaña se entera por donde siempre.
   */
  setLanguage: (next: Language) => void
}

const LanguageContext = createContext<LanguageValue | null>(null)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => activeLanguage())

  /* Si lo cambian en el panel con el editor abierto en otra pestaña, aquí se
     entera: `onLanguageChange` escucha tanto el cambio de esta página como el
     `storage` que dispara la otra. */
  useEffect(() => onLanguageChange(() => {
    const next = readLanguage()
    whenCatalog(next, () => {
      currentLanguage = next
      setLanguage(next)
    })
  }), [])

  /* El atributo del documento: de ahí salen la partición de palabras, el
     corrector y lo que anuncia un lector de pantalla. */
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const changeLanguage = useCallback((next: Language) => {
    persistLanguage(next)
    whenCatalog(next, () => {
      currentLanguage = next
      setLanguage(next)
    })
  }, [])

  const value = useMemo<LanguageValue>(() => ({
    language,
    setLanguage: changeLanguage,
    t: createTranslator(language, catalogs(), (key) => {
      /* Ruidoso a propósito, y sólo en desarrollo: una clave que falta se ve en
         pantalla —se pinta la clave— pero a quien la ve le falta saber dónde
         mirar. En producción no se avisa: no es un error de quien usa esto. */
      if (import.meta.env.DEV) console.warn(`[i18n] falta la clave «${key}»`)
    }),
    fmt: formatters(language),
  }), [changeLanguage, language])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

/**
 * El traductor de la pantalla.
 *
 * Sin proveedor devuelve uno del idioma por defecto en vez de lanzar: hay
 * pantallas que se montan sueltas en las pruebas, y reventar ahí convierte un
 * texto en un fallo de arranque.
 */
export function useI18n(): LanguageValue {
  const value = useContext(LanguageContext)
  if (value) return value
  return {
    language: DEFAULT_LANGUAGE,
    t: createTranslator(DEFAULT_LANGUAGE, catalogs()),
    fmt: formatters(DEFAULT_LANGUAGE),
    /* Sin proveedor no hay dónde guardarlo: se calla en vez de reventar, igual
       que el resto de este respaldo. */
    setLanguage: () => {},
  }
}
